import React from 'react';
import { JobExecution, ListExecutionsResponseBody } from '@lct-jobs/core';
import { useSnackbar } from 'hooks/useSnackbar';
import { apiUrl } from '../../utils/apiUrl';
import useRestApi from '../../hooks/useRestApi';
import { useTenantContext } from '../TenantContext';
import { useLearnInstance } from 'hooks/useLearnInstance';
import { useJobRegion } from 'contexts/JobRegionContext';

export interface JobExecutionContextProps {
  readonly tenantId: string;
  readonly executions: JobExecution[];
  readonly refresh: () => void;
  readonly loadMore: () => void;
  readonly nextPageToken?: string;
  readonly siteNextPageToken?: string;
  readonly loading: boolean;
  readonly setExecutionId: (id?: string) => void;
  readonly learnInstance?: any;
}

export const JobExecutionContext = React.createContext<JobExecutionContextProps>({
  tenantId: '',
  executions: [],
  refresh: () => {
    throw new Error('JobExecutionContextProvider not found');
  },
  loadMore: () => {
    throw new Error('JobExecutionContextProvider not found');
  },
  loading: false,
  setExecutionId: () => {
    throw new Error('JobExecutionContextProvider not found');
  },
});

export const useJobExecutionContext = () => React.useContext(JobExecutionContext);

export const JobExecutionContextProvider: React.FunctionComponent = (props) => {
  const { children } = props;
  const tenantContext = useTenantContext();
  const { lctApiUrl, isJobRegionReady } = useJobRegion();
  const [nextPageToken, setNextPageToken] = React.useState<string | undefined>(undefined);
  const [siteNextPageToken, setSiteNextPageToken] = React.useState<string | undefined>(undefined);
  // eslint-disable-next-line prefer-const
  let [executionId, setExecutionId] = React.useState<string | undefined>(undefined);
  const [initDone, setInitDone] = React.useState(false);
  const executionApiUrl = lctApiUrl(`tenants/${tenantContext.tenantId}/jobManager/executions`);

  const executionApi = useRestApi<ListExecutionsResponseBody>(executionApiUrl, { manual: true });

  const isLoadingMoreRef = React.useRef(false);

  const { learnInstanceId, learnInstance } = useLearnInstance();

  const childJobExecutions = useRestApi<ListExecutionsResponseBody>(
    apiUrl('lct', `tenants/${learnInstanceId}/jobManager/executions`),
  );

  const { enqueueSnackbar } = useSnackbar();

  const { data: executionData, error } = executionApi;
  const { data: siteExecutionData } = childJobExecutions;

  const [executions, setExecutions] = React.useState<JobExecution[]>([]);

  // When the job region changes the URL changes. Clear the accumulated executions so data
  // from the previous region isn't merged with the incoming data from the new region.
  const prevExecutionApiUrl = React.useRef(executionApiUrl);
  React.useEffect(() => {
    if (!isJobRegionReady) return;

    const urlChanged = prevExecutionApiUrl.current !== executionApiUrl;
    prevExecutionApiUrl.current = executionApiUrl;
    if (urlChanged || !initDone) {
      isLoadingMoreRef.current = false;
      setExecutions([]);
      setNextPageToken(undefined);
      setSiteNextPageToken(undefined);
      executionApi.fetch({ method: 'GET' }).catch(() => {});
    }
    if (!initDone) {
      setInitDone(true);
    }
  }, [executionApiUrl, isJobRegionReady, initDone, executionApi]);

  const refresh = () => {
    if (!isJobRegionReady || !initDone) return;
    isLoadingMoreRef.current = false;
    setExecutions([]);
    setNextPageToken(undefined);
    setSiteNextPageToken(undefined);
    if (!executionId) {
      executionApi.fetch({
        method: 'GET',
      });
    }
    if (learnInstanceId && tenantContext.tenant?.tenantType === 'Learn' && !executionId) {
      childJobExecutions.fetch();
    }
  };

  const loadMore = (options?: { limit?: number }) => {
    if (!isJobRegionReady || !initDone) return;
    if (!nextPageToken && !siteNextPageToken) return;

    isLoadingMoreRef.current = true;

    if (nextPageToken) {
      executionApi.fetch({
        method: 'GET',
        params: { nextPageToken, ...(options?.limit ? { limit: options.limit } : {}) },
      });
    }
    if (siteNextPageToken) {
      childJobExecutions.fetch({
        method: 'GET',
        params: {
          nextPageToken: siteNextPageToken,
          ...(options?.limit ? { limit: options.limit } : {}),
        },
      });
    }
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const context: JobExecutionContextProps = {
    tenantId: tenantContext.tenantId,
    executions,
    refresh,
    loadMore,
    nextPageToken,
    siteNextPageToken,
    loading: executionApi.loading || childJobExecutions.loading,
    learnInstance,
    setExecutionId: (id) => {
      setExecutionId(id);
      if (!id) {
        executionId = undefined;
      }
    },
  };

  React.useEffect(() => {
    if (executionData?.executions || siteExecutionData?.executions) {
      setExecutions((prevExecutions) => {
        const newExecutions = [
          ...(executionData?.executions ?? []),
          ...(siteExecutionData?.executions ?? []),
        ];
        const base = isLoadingMoreRef.current ? prevExecutions : [];
        const newIds = newExecutions.map((e) => e.executionId);
        const combinedExecutions = [
          ...base.filter((e) => !newIds.includes(e.executionId)),
          ...newExecutions,
        ];
        combinedExecutions.sort(
          (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
        );
        return combinedExecutions;
      });
    }
    if (executionApi.data) {
      setNextPageToken(executionApi.data.nextPageToken);
      executionApi.data = undefined;
    }
    if (childJobExecutions.data) {
      setSiteNextPageToken(childJobExecutions.data.nextPageToken);
      childJobExecutions.data = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionData, executionApi.data, siteExecutionData, childJobExecutions.data]);

  React.useEffect(() => {
    if (error) {
      enqueueSnackbar(`Failed to load Job Executions: ${error.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, error]);

  return <JobExecutionContext.Provider value={context}>{children}</JobExecutionContext.Provider>;
};
